import * as React from 'react'
import { Params, useLocation, useParams } from 'react-router-dom'

import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { isSameMonth, isSameWeek } from 'date-fns'
import moment from 'moment'

import { dateToString } from 'modules/core/lib/dates'
import { range } from 'modules/core/lib/frequencies'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { TimeTrackingViewType } from './useCustomPeriodPath'

export const Cycle = Object.freeze({
  daily: 'daily',
  weekly: 'weekly',
  fortnightly: 'fortnightly',
  monthly: 'monthly',
})
export type Cycle = (typeof Cycle)[keyof typeof Cycle]

type DateNavigatorValue = [Date, Date, Cycle]
type DateRange = [Date, Date]

interface ContextType {
  dateFrom: Date
  dateTo: Date
  cycle: Cycle
  dateToOrToday: Date
  stringDateFrom: string
  stringDateTo: string
  pathParams: TimeTrackingViewType
  onDateChange: (newRange: DateRange) => DateNavigatorValue
  resetAttendanceDateNavigator: () => DateNavigatorValue
}

const DATE_FORMAT = 'YYYY-MM-DD'

const Context = React.createContext<ContextType | null>(null)

const toCycleEnum = (periodType: string | undefined): Cycle => {
  switch (periodType) {
    case 'daily':
      return Cycle.daily
    case 'weekly':
      return Cycle.weekly
    case 'fortnightly':
      return Cycle.fortnightly
    case 'monthly':
      return Cycle.monthly
    default:
      return Cycle.monthly
  }
}

const fetchDates = (
  params: Readonly<Params<string>>,
  viewType: TimeTrackingViewType | null
): DateNavigatorValue => {
  const today = moment()
  const year = params.year || viewType?.year || today.format('YY')
  const month = params.month || viewType?.month || today.format('MM')
  const day = params.day || viewType?.day || today.format('DD')
  const cycleValue = params.periodType ?? viewType?.periodType
  const defaultCycle = toCycleEnum(cycleValue)
  let dateFrom = moment(`${year}-${month}-${day}`, DATE_FORMAT)

  if (!dateFrom.isValid()) dateFrom = moment(today, DATE_FORMAT)

  const [from, to] = range(dateFrom, defaultCycle, false)

  return [
    new Date(moment.utc(from).toISOString()),
    new Date(moment.utc(to).toISOString()),
    defaultCycle,
  ]
}

const fetchDateOrToday = (cycle: Cycle, dateTo: Date): Date => {
  const today = new Date(moment.utc().toISOString())
  const isCurrentWeek = cycle === Cycle.weekly && isSameWeek(today, dateTo, { weekStartsOn: 1 }) // Week only starts on Monday right now
  const isCurrentMonth = cycle === Cycle.monthly && isSameMonth(today, dateTo)
  const isCurrentPeriod = isCurrentWeek || isCurrentMonth
  // If the current date range is within the current period and the specified end date is in the future (beyond today),
  // use today's date as the end date. Otherwise, use the specified end date.
  return isCurrentPeriod && dateTo > today ? today : dateTo
}

const fetchPathParams = (dateFrom: Date, cycle: Cycle) => {
  return {
    periodType: cycle,
    year: dateFrom.getFullYear(),
    month: dateFrom.getMonth() + 1,
    day: dateFrom.getDate(),
  }
}

export const AttendanceDateNavigatorProvider = ({ children }: { children: React.ReactNode }) => {
  const params = useParams()
  const [viewType, setViewType] = useLocalStorage<TimeTrackingViewType>(
    LOCAL_STORAGE_KEYS.timeTrackingViewType
  )
  const [defaultDateFrom, defaultDateTo, defaultCycle] = fetchDates(params, viewType)
  const [dateFrom, setDateFrom] = React.useState<Date>(defaultDateFrom)
  const [dateTo, setDateTo] = React.useState<Date>(defaultDateTo)
  const [cycle, setCycle] = React.useState<Cycle>(defaultCycle)
  const [dateToOrToday, setDateToOrToday] = React.useState<Date>(fetchDateOrToday(cycle, dateTo))
  const [stringDateFrom, setStringDateFrom] = React.useState<string>(
    dateToString(defaultDateFrom, 'backend')
  )
  const [stringDateTo, setStringDateTo] = React.useState<string>(
    dateToString(defaultDateTo, 'backend')
  )
  const [pathParams, setPathParams] = React.useState<TimeTrackingViewType>(
    fetchPathParams(dateFrom, cycle)
  )

  const { pathname } = useLocation()
  const isTimeTrackingPage = pathname.includes('time-tracking') || pathname == '/attendance'

  React.useEffect(() => {
    if (!isTimeTrackingPage) return
    if (!params.periodType || !params.year || !params.month || !params.day) return

    setViewType(fetchPathParams(defaultDateFrom, defaultCycle))
    // Only depend on URL params. Do NOT add defaultDateFrom/defaultCycle (new Date() every render)
    // or setViewType (useLocalStorage returns a new setter every render) to avoid infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setViewType intentionally omitted
  }, [isTimeTrackingPage, params.periodType, params.year, params.month, params.day])

  const onDateChange = (newRange: DateRange): DateNavigatorValue => {
    const [newDateFrom, newDateTo] = newRange

    const daysDifference = moment(newDateTo).diff(newDateFrom, 'days')

    const newCycle =
      +newDateFrom === +newDateTo
        ? Cycle.daily
        : daysDifference > 14
          ? Cycle.monthly
          : daysDifference > 7
            ? Cycle.fortnightly
            : Cycle.weekly

    const dateParams = fetchPathParams(newDateFrom, newCycle)

    setDateFrom(newDateFrom)
    setDateTo(newDateTo)
    setCycle(newCycle)
    setDateToOrToday(fetchDateOrToday(newCycle, newDateTo))
    setPathParams(dateParams)
    setStringDateFrom(dateToString(newDateFrom, 'backend'))
    setStringDateTo(dateToString(newDateTo, 'backend'))

    if (isTimeTrackingPage) setViewType(dateParams)

    return [newDateFrom, newDateTo, newCycle]
  }

  const resetAttendanceDateNavigator = (): DateNavigatorValue => {
    let initialDate = moment()
    let initialCycle = cycle

    if (isTimeTrackingPage) {
      const { day, month, year, periodType } = viewType ?? {}

      if (periodType) {
        initialCycle = toCycleEnum(periodType)
      }
      if (year && month && day) {
        initialDate = moment(`${year}-${month}-${day}`, DATE_FORMAT)
      }
    }

    const [initialDateFrom, initialDateTo] = range(initialDate, initialCycle, true)
    const initialDateRange: DateRange = [
      new Date(moment.utc(initialDateFrom).toISOString()),
      new Date(moment.utc(initialDateTo).toISOString()),
    ]

    return onDateChange(initialDateRange)
  }

  return (
    <Context.Provider
      value={{
        dateFrom,
        dateTo,
        cycle,
        dateToOrToday,
        stringDateFrom,
        stringDateTo,
        pathParams,
        onDateChange,
        resetAttendanceDateNavigator,
      }}
    >
      {children}
    </Context.Provider>
  )
}

export const useAttendanceDateNavigator = (): ContextType => {
  const DateNavigator = React.useContext(Context)

  if (!DateNavigator) {
    throw new Error('useDateNavigator must be used within an AttendanceDateNavigatorProvider')
  }

  return DateNavigator
}
